import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AdminLandingPageLink } from '@storefronts/common-types'
import { useCallback, useMemo, useState } from 'react'
import { SelectLandingPageLinks } from '../landingpagelinks/SelectLandingPageLinks'
import { X } from 'lucide-react'

function landingPageLinkPickerFieldsEqual(
    a: AdminLandingPageLink,
    b: AdminLandingPageLink,
): boolean {
    const aOverride = a.defaultVariantOverrideId ?? null
    const bOverride = b.defaultVariantOverrideId ?? null
    return aOverride === bOverride
}

type LandingPageLinksSelectionModalProps = {
    isOpen: boolean
    onClose: () => void
    defaultSelectedLinks: AdminLandingPageLink[]
    ignoredShopifyIds?: string[]
    onSave: (links: AdminLandingPageLink[]) => Promise<void>
    modalTitle: string
    showFeatureBadge?: boolean
    showStorefrontTab?: boolean
    noProductsSuggestion?: string
    /** If provided, called before removing a link from the list. Return false to cancel removal. */
    onBeforeRemove?: (link: AdminLandingPageLink) => Promise<boolean>
    /** Main storefront product list only; see SelectLandingPageLinks. */
    showStorefrontDefaultVariantUI?: boolean
}

export const LandingPageLinksSelectionModal: React.FC<
    LandingPageLinksSelectionModalProps
> = ({
    isOpen,
    onClose,
    defaultSelectedLinks,
    ignoredShopifyIds,
    onSave,
    modalTitle,
    showFeatureBadge = true,
    showStorefrontTab = false,
    noProductsSuggestion,
    onBeforeRemove,
    showStorefrontDefaultVariantUI = false,
}) => {
    const [selectedLinks, setSelectedLinks] =
        useState<AdminLandingPageLink[]>(defaultSelectedLinks)
    const [isSaving, setIsSaving] = useState(false)
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
        useState(false)

    const [prevDefaultSelectedLinks, setPrevDefaultSelectedLinks] =
        useState(defaultSelectedLinks)
    if (defaultSelectedLinks !== prevDefaultSelectedLinks) {
        setPrevDefaultSelectedLinks(defaultSelectedLinks)
        setSelectedLinks(defaultSelectedLinks)
    }

    // Check if there are unsaved changes by comparing selectedLinks with defaultSelectedLinks
    const hasUnsavedChanges = useMemo(() => {
        if (selectedLinks.length !== defaultSelectedLinks.length) {
            return true
        }

        for (let i = 0; i < selectedLinks.length; i++) {
            const sel = selectedLinks[i]
            const def = defaultSelectedLinks[i]
            if (sel.linkHash !== def.linkHash) {
                return true
            }
            if (!landingPageLinkPickerFieldsEqual(sel, def)) {
                return true
            }
        }

        return false
    }, [selectedLinks, defaultSelectedLinks])

    // Handle close attempt - show confirmation if there are unsaved changes
    const handleCloseAttempt = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowUnsavedChangesDialog(true)
        } else {
            onClose()
        }
    }, [hasUnsavedChanges, onClose])

    // Discard changes and close modal
    const handleDiscardChanges = useCallback(() => {
        setShowUnsavedChangesDialog(false)
        setSelectedLinks(defaultSelectedLinks)
        onClose()
    }, [defaultSelectedLinks, onClose])

    // Save changes and close modal
    const handleSaveChanges = useCallback(async () => {
        setShowUnsavedChangesDialog(false)
        setIsSaving(true)
        try {
            await onSave(selectedLinks)
            onClose()
        } finally {
            setIsSaving(false)
        }
    }, [selectedLinks, onSave, onClose])

    const _onSave = async () => {
        setIsSaving(true)
        try {
            await onSave(selectedLinks)
            onClose()
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <>
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open) handleCloseAttempt()
                }}
            >
                <DialogContent className="mx-4 flex h-[90vh] max-h-[90vh] w-[95vw] max-w-[90rem] flex-col overflow-hidden rounded-xl p-0 shadow-xl bg-card">
                    <DialogHeader className="shrink-0 border-b px-6 py-4 border-border">
                        <DialogTitle className="text-xl font-medium">
                            {modalTitle}
                        </DialogTitle>
                    </DialogHeader>
                    <button
                        className="absolute right-4 top-4 rounded-sm hover:bg-accent"
                        onClick={(e) => {
                            e.preventDefault()
                            handleCloseAttempt()
                        }}
                    >
                        <X className="size-4" />
                    </button>
                    <div className="flex flex-1 flex-col overflow-hidden p-0">
                        <SelectLandingPageLinks
                            selectedLinks={selectedLinks}
                            setSelectedLinks={setSelectedLinks}
                            ignoredShopifyIds={ignoredShopifyIds}
                            showFeatureBadge={showFeatureBadge}
                            showStorefrontTab={showStorefrontTab}
                            noProductsSuggestion={noProductsSuggestion}
                            onBeforeRemove={onBeforeRemove}
                            showStorefrontDefaultVariantUI={
                                showStorefrontDefaultVariantUI
                            }
                        />
                    </div>
                    <DialogFooter className="shrink-0 border-t px-6 py-4 border-border">
                        <Button
                            onClick={handleCloseAttempt}
                            variant="outline"
                            className="mr-3"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={isSaving}
                            variant="blackcrow"
                            onClick={_onSave}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Unsaved Changes Confirmation Dialog */}
            <AlertDialog
                open={showUnsavedChangesDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowUnsavedChangesDialog(false)
                    }
                }}
            >
                <AlertDialogContent
                    className="z-[1500]"
                    overlayClassName="z-[1500]"
                >
                    {/* Close button */}
                    <button
                        onClick={() => setShowUnsavedChangesDialog(false)}
                        className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity ring-offset-background hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                    >
                        <X className="size-4" />
                    </button>

                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            If you leave this page, any changes you have made
                            will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleDiscardChanges}>
                            Discard changes and leave
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleSaveChanges}
                            className="hover:bg-blackcrow/90 bg-blackcrow text-blackcrow-foreground"
                        >
                            Save changes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}
